import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { RequestChatIcon, RequestType, UserCell } from '@strata/react-chat';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { Icon } from '@strata/react-status-icons';
import { PayerRequestStatusIndicator } from '@strata/react-status-indicators';
import { type ComponentProps, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { ViewsControls } from 'components/shared/ViewsControls';
import { PayerRequestNumber } from 'components/treatments/PayerRequestNumber';
import { getActionButtonPageTitleLabel } from 'services/payerRequests';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useUserNameMap } from 'store/api/authApi';
import { type OpenRequestSummary, useGetOpenRequestsByTypeQuery } from 'store/api/payerRequestApi';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'createDate', desc: true }];

const customSearchValueHandlers: ComponentProps<
    typeof GridWithFilters<OpenRequestSummary>
>['customSearchValueHandlers'] = {
    createDate: (value) => formatDate(value, DateFormats.DATE_TIME),
    requestStatus: (value) => PayerRequestStatusIndicator.StatusMessages[value],
};

interface RequestsGridProps {
    requestType: Exclude<
        (typeof RequestType)[keyof typeof RequestType],
        typeof RequestType.ARCHIVE
    >;
    pbmOnlyAccess?: boolean;
}

const filterCategories = ({
    requestType,
    pbmOnlyAccess,
}: RequestsGridProps): FilterCategory<OpenRequestSummary>[] => [
    ...(requestType !== RequestType.OTHER
        ? [
              {
                  key: 'requestSubType',
                  title: 'Request Type',
              } as FilterCategory<OpenRequestSummary>,
          ]
        : []),
    ...(requestType === RequestType.ESCALATION
        ? [{ key: 'typeLevel2', title: 'Request Subtype' } as FilterCategory<OpenRequestSummary>]
        : []),
    {
        key: 'requestStatus',
        title: 'Status',
        valueDisplayTextMap: PayerRequestStatusIndicator.StatusMessages,
    },
    { key: 'payerAssignedFullName', title: 'Payer Assignee' },
    {
        key: 'urgent',
        title: 'Request Urgency',
        customValues: [{ value: 'true', text: 'Urgent' }],
    },
    ...(pbmOnlyAccess
        ? [
              {
                  key: 'pbmOnly',
                  title: 'Visibility',
                  customValues: [{ value: 'true', text: 'PBM Only' }],
              } as FilterCategory<OpenRequestSummary>,
          ]
        : []),
];

const columns = ({ requestType }: RequestsGridProps): ColumnDef<OpenRequestSummary>[] => [
    {
        ...{
            [RequestType.ESCALATION]: { accessorKey: 'memberId', header: 'Member ID' },
            [RequestType.CHANGE]: { accessorKey: 'requestSubType', header: 'Request Type' },
            [RequestType.OTHER]: { accessorKey: 'summary', header: 'Summary' },
        }[requestType],
        size: 180,
        cell: ({ getValue }: { getValue: () => string | undefined }) => (
            <PrimaryData data={getValue()} />
        ),
    } as ColumnDef<OpenRequestSummary>,
    {
        ...{
            [RequestType.ESCALATION]: { accessorKey: 'requestSubType', header: 'Request Type' },
            [RequestType.CHANGE]: { accessorKey: 'summary', header: 'Summary' },
            [RequestType.OTHER]: { accessorKey: 'additionalNotes', header: 'Description' },
        }[requestType],
        size: 180,
    } as ColumnDef<OpenRequestSummary>,
    ...(requestType === RequestType.ESCALATION
        ? [
              {
                  accessorKey: 'typeLevel2',
                  header: 'Request Subtype',
                  size: 160,
              } as ColumnDef<OpenRequestSummary>,
          ]
        : []),
    {
        accessorKey: 'createDate',
        header: 'Created On',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'lastUpdated',
        header: 'Last Updated',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'resolvedDate',
        header: 'Resolved Date',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'submitterUpn',
        header: 'Created By',
        size: 150,
        cell: ({ getValue, row }) => (
            <UserCell upn={getValue()} fullName={row.original.submitterFullName} small />
        ),
    },
    {
        accessorKey: 'isUnread',
        header: () => <Icon icon={IconNames.CHAT} />,
        enableSorting: false,
        size: 48,
        cell: ({ getValue }) => <RequestChatIcon isUnread={getValue()} />,
    },
    {
        accessorKey: 'urgent',
        header: () => <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} />,
        enableSorting: false,
        size: 48,
        cell: ({ getValue }) =>
            getValue() ? (
                <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} title="Urgent" />
            ) : null,
    },
    {
        accessorKey: 'requestNumber',
        header: 'Request #',
        size: 90,
        cell: ({ getValue, row }) => (
            <PayerRequestNumber text={getValue()} pbmOnly={row.original.pbmOnly} />
        ),
    },
    {
        accessorKey: 'payerAssignedUpn',
        header: 'Payer Assignee',
        size: 120,
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={
                    getValue() ? (
                        (row.original.payerAssignedFullName ?? getValue())
                    ) : (
                        <span className="sds_request-assigned-to--unassigned">Unassigned</span>
                    )
                }
            />
        ),
    },
    {
        accessorKey: 'requestStatus',
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => (
            <PayerRequestStatusIndicator
                status={
                    getValue() === PayerRequestStatusIndicator.Statuses.RESOLVED
                        ? PayerRequestStatusIndicator.Statuses.MONITORING
                        : getValue()
                }
            />
        ),
    },
];

export const RequestsGrid = ({ requestType, pbmOnlyAccess = false }: RequestsGridProps) => {
    const navigate = useNavigate();

    const { payerId, segmentIds } = useAppSelector((state) => state.auth);
    const { segmentId } = useAppSelector((state) => state.views);

    const { data, isFetching } = useGetOpenRequestsByTypeQuery({
        payerId: payerId!,
        requestType,
        segmentIds: segmentId ? [segmentId] : segmentIds,
    });
    const userNameMap = useUserNameMap({ payerId: payerId! });
    const requests = useMemo(
        () =>
            data?.requests?.map((request) => ({
                ...request,
                submitterFullName: userNameMap?.[request.submitterUpn],
                payerAssignedFullName:
                    request.payerAssignedUpn && userNameMap?.[request.payerAssignedUpn],
            })),
        [data?.requests, userNameMap],
    );

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(requestType, initialSortProperties);

    return (
        <GridWithFilters
            headerContent={
                (data?.filteredCount ?? 0) > 0 ? (
                    <div className="margin__bottom--medium">
                        <InlineMessage
                            intent={InlineMessage.Intent.INFO}
                            label={`The ${data!.requests?.length} requests viewable below do not include ${data!.filteredCount} restricted requests hidden from view.`}
                        />
                    </div>
                ) : undefined
            }
            viewsPaneContent={<ViewsControls />}
            data={requests}
            isLoading={isFetching}
            columns={columns({ requestType })}
            sortProperties={sort}
            onSort={updateGridSort}
            enableSearch
            searchText={search}
            onSearch={updateGridSearch}
            customSearchValueHandlers={customSearchValueHandlers}
            filterCategories={filterCategories({ requestType, pbmOnlyAccess })}
            activeFilters={activeFilters}
            onFilter={updateGridActiveFilters}
            filterPaneRenderWidth={filterPaneWidth}
            onFilterPaneResize={updateFilterPaneWidth}
            onRowClick={(rowData) =>
                navigate(AppRoutes.requestDetails(rowData.requestId), {
                    state: {
                        backToDestination: {
                            text: getActionButtonPageTitleLabel({
                                requestType,
                                requestStatus: rowData.requestStatus,
                            }),
                        },
                    },
                })
            }
        />
    );
};
